import React, { useEffect, useState, useCallback } from 'react';
import { Card, Row, Col, Rate, Empty, Alert, Typography, List, Button, Tag, Space, Progress } from 'antd';
import {
  TrophyOutlined, StarOutlined, CheckCircleOutlined, ClockCircleOutlined,
  CalendarOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import api from '../../../services/api';
import RateGameModal from './RateGameModal';

const { Text, Title } = Typography;
const BRAND = '#25D366';
const BRAND_DARK = '#128C7E';

const StatCard = ({ icon, gradient, label, value, suffix }) => (
  <Card
    bordered={false}
    style={{
      background: '#fff',
      boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
      borderRadius: 14, overflow: 'hidden', height: '100%',
    }}
    bodyStyle={{ padding: 18 }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{
        width: 44, height: 44, borderRadius: 12,
        background: gradient, display: 'grid', placeItems: 'center',
        color: '#fff', fontSize: 20, flexShrink: 0,
        boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
      }}>{icon}</div>
      <div>
        <div style={{ fontSize: 11, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#222', lineHeight: 1.2 }}>
          {value}{suffix ? <Text type="secondary" style={{ fontSize: 13, marginLeft: 3, fontWeight: 500 }}>{suffix}</Text> : null}
        </div>
      </div>
    </div>
  </Card>
);

const StatsTab = () => {
  const [data, setData] = useState(null);
  const [pending, setPending] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rateModal, setRateModal] = useState({ open: false, game: null });

  const loadStats = useCallback(async () => {
    try {
      const r = await api.get('/community/player/stats');
      setData(r.data);
      setDisabled(false);
    } catch (err) {
      if (err?.response?.status === 403) setDisabled(true);
    }
  }, []);

  const loadPending = useCallback(async () => {
    try {
      const r = await api.get('/community/player/pending-ratings');
      setPending(r.data?.pending || []);
    } catch { /* silencioso */ }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadStats(), loadPending()]);
      setLoading(false);
    })();
  }, [loadStats, loadPending]);

  if (loading) return <Card loading />;

  if (disabled) {
    return (
      <Alert
        type="info" showIcon style={{ borderRadius: 12 }}
        message="Estatísticas desativadas"
        description="O organizador do seu time ainda não ativou as estatísticas dos jogadores. Peça pra ele ligar em Meu Time → Estatísticas do time."
      />
    );
  }

  if (!data) return <Empty description="Sem dados" />;

  const freq = data.frequency || {};
  const rating = data.rating_received || {};
  const stats = data.stats || {};

  return (
    <>
      {pending.length > 0 && (
        <Card
          bordered={false}
          style={{
            marginBottom: 16, borderRadius: 14,
            background: 'linear-gradient(135deg, #FFF4E5 0%, #FFE4B5 100%)',
            border: '1px solid #FFB84D',
          }}
          bodyStyle={{ padding: 18 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <ClockCircleOutlined style={{ color: '#FA8C16', fontSize: 20 }} />
            <Text strong style={{ fontSize: 15 }}>Avaliações pendentes</Text>
            <Tag color="orange" style={{ margin: 0 }}>{pending.length}</Tag>
            <Text type="secondary" style={{ fontSize: 12, marginLeft: 'auto' }}>Até 24h após o jogo</Text>
          </div>
          <List
            dataSource={pending}
            renderItem={(g) => (
              <List.Item
                style={{ padding: '10px 0', borderBottom: '1px solid rgba(0,0,0,0.06)' }}
                actions={[
                  <Button
                    type="primary" icon={<StarOutlined />}
                    onClick={() => setRateModal({ open: true, game: g })}
                    style={{ background: '#FA8C16', borderColor: '#FA8C16', fontWeight: 600 }}
                  >Avaliar</Button>,
                ]}
              >
                <List.Item.Meta
                  title={<Space wrap>{g.date_of_game} <Tag>{g.team_name}</Tag></Space>}
                  description={<Text type="secondary">Restam ~{g.hours_left}h</Text>}
                />
              </List.Item>
            )}
          />
        </Card>
      )}

      {/* Cards de números */}
      <Row gutter={[14, 14]}>
        <Col xs={12} md={6}>
          <StatCard
            icon={<CalendarOutlined />}
            gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            label="Convocações"
            value={freq.games_convocados || 0}
          />
        </Col>
        <Col xs={12} md={6}>
          <StatCard
            icon={<CheckCircleOutlined />}
            gradient={`linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`}
            label="Confirmações"
            value={freq.confirmed || 0}
          />
        </Col>
        <Col xs={12} md={6}>
          <StatCard
            icon={<ThunderboltOutlined />}
            gradient="linear-gradient(135deg, #f6d365 0%, #fda085 100%)"
            label="Presença"
            value={freq.confirmation_rate_pct || 0}
            suffix="%"
          />
        </Col>
        <Col xs={12} md={6}>
          <StatCard
            icon={<TrophyOutlined />}
            gradient="linear-gradient(135deg, #ffd700 0%, #ff8c00 100%)"
            label="MVP"
            value={data.awards?.mvp_count || 0}
          />
        </Col>
      </Row>

      {/* Rating recebido + Progress presença */}
      <Row gutter={[14, 14]} style={{ marginTop: 14 }}>
        <Col xs={24} md={12}>
          <Card
            bordered={false}
            style={{ borderRadius: 14, boxShadow: '0 4px 16px rgba(0,0,0,0.04)', height: '100%' }}
            title={<Space><StarOutlined style={{ color: '#faad14' }} /> <span>Reputação</span></Space>}
          >
            {rating.avg ? (
              <div style={{ textAlign: 'center', padding: '12px 0' }}>
                <div style={{ fontSize: 42, fontWeight: 800, color: '#faad14', lineHeight: 1 }}>
                  {Number(rating.avg).toFixed(2)}
                </div>
                <Rate allowHalf disabled value={Number(rating.avg)} style={{ fontSize: 18, marginTop: 8 }} />
                <div style={{ marginTop: 6 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {rating.total} {rating.total === 1 ? 'avaliação' : 'avaliações'} recebidas
                  </Text>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '18px 0', color: '#8c8c8c' }}>
                <StarOutlined style={{ fontSize: 40, opacity: 0.3 }} />
                <div style={{ marginTop: 12 }}>Sem avaliações ainda.</div>
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card
            bordered={false}
            style={{ borderRadius: 14, boxShadow: '0 4px 16px rgba(0,0,0,0.04)', height: '100%' }}
            title={<Space><ThunderboltOutlined style={{ color: BRAND }} /> <span>Sua frequência</span></Space>}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 20, padding: '4px 0' }}>
              <Progress
                type="circle" size={100}
                percent={freq.confirmation_rate_pct || 0}
                strokeColor={{ '0%': BRAND, '100%': BRAND_DARK }}
                format={(p) => <span style={{ fontWeight: 800, fontSize: 20 }}>{p}%</span>}
              />
              <div>
                <div><Text strong>{freq.confirmed || 0}</Text> confirmações</div>
                <div><Text strong>{freq.games_convocados || 0}</Text> convocações no total</div>
                <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
                  Quanto maior a taxa, mais alto no ranking.
                </div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Placeholder futuro */}
      <Card
        bordered={false}
        style={{ marginTop: 14, borderRadius: 14, boxShadow: '0 4px 16px rgba(0,0,0,0.04)' }}
        title={<Space><TrophyOutlined /> <span>Números no jogo</span></Space>}
        extra={stats.games_with_stats > 0
          ? <Text type="secondary" style={{ fontSize: 12 }}>{stats.games_with_stats} {stats.games_with_stats === 1 ? 'jogo lançado' : 'jogos lançados'}</Text>
          : null}
      >
        {(!stats.games_with_stats) && (
          <Alert
            type="info" showIcon
            message="Aguardando o organizador lançar os dados dos seus jogos"
            style={{ borderRadius: 10, marginBottom: 12 }}
          />
        )}
        <Row gutter={[12, 12]}>
          {[
            ['⚽', 'Gols', stats.goals, '#52c41a'],
            ['🅰️', 'Assistências', stats.assists, '#1890ff'],
            ['🟨', 'Amarelos', stats.yellow_cards, '#faad14'],
            ['🟦', 'Azuis', stats.blue_cards, '#1677ff'],
            ['🟥', 'Vermelhos', stats.red_cards, '#f5222d'],
            ['🚨', 'Faltas', stats.fouls, '#722ed1'],
            ['🥅', 'Gols contra', stats.own_goals, '#8c8c8c'],
          ].map(([emoji, label, val, color]) => (
            <Col key={label} xs={12} sm={8} md={4}>
              <div style={{
                background: '#fff', padding: 14, borderRadius: 10, textAlign: 'center',
                border: '1px solid #eef1f4',
              }}>
                <div style={{ fontSize: 22 }}>{emoji}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: (val || 0) > 0 ? color : '#bfbfbf' }}>{val ?? 0}</div>
                <div style={{ fontSize: 11, color: '#8c8c8c' }}>{label}</div>
              </div>
            </Col>
          ))}
        </Row>
      </Card>

      <RateGameModal
        open={rateModal.open}
        game={rateModal.game}
        onClose={() => setRateModal({ open: false, game: null })}
        onSaved={() => { loadPending(); loadStats(); }}
      />
    </>
  );
};

export default StatsTab;
